import type { Entries } from './catalog';

/**
 * The copy of signing in and up, passwords, confirmation, recovery and changing an address (ACC-01…
 * ACC-04, ACC-09…ACC-11, ACC-17, ACC-19, ARR-25).
 *
 * Each area keeps its own file so its keys travel with its screens; a key here must not repeat one of
 * `en.ts`, `es.ts` or another area.
 */
export const credentials: Record<'en' | 'es', Entries> = {
	en: {
		'signin.create': 'Create an account',
		'signin.unconfirmed': 'Confirm your address first. Open the link we sent to {email}.',
		'signin.resend': 'Send the link again',
		'signin.resend.sent': 'Sent. Check {email}, including the spam folder.',
		'signin.resend.wait': { one: 'You can send it again in 1 second.', other: 'You can send it again in {count} seconds.' },
		'signin.recover.wait': { one: 'You can ask again in 1 second.', other: 'You can ask again in {count} seconds.' },
		'signin.challenge': 'Checking this browser before the next attempt…',
		'signin.challenge.short': 'Several attempts failed. Try again.',
		'signin.matches': 'An account with this address already exists. Sign in with {methods}, then add {provider} from Sign-in and security.',
		'signin.matches.short': 'An account with this address already exists. Sign in the way you did before.',
		'signin.erased.at': 'This account was deleted on {date}.',
		'signin.erased.short': 'This account was deleted.',
		'signin.or.email': 'or with your email',
		'identities.provider.another': 'the new sign-in method',

		'signup.title': 'Create your Beyond account',
		'signup.lead': 'One account for every Beyond product.',
		'signup.name': 'Your name',
		'signup.name.help': 'What other members of your organizations see.',
		'signup.submit': 'Create account',
		'signup.have': 'Already have an account?',
		'signup.signin': 'Sign in',
		'signup.sent': 'Check {email} and open the link to finish creating your account.',

		'password.show': 'Show password',
		'password.hide': 'Hide password',
		'password.rule.length': 'At least {min} characters',
		'password.rule.common': 'Not a common or leaked password',
		'password.weak': 'Choose a stronger password.',
		'password.weak.short': 'Use at least {min} characters.',
		'password.weak.long': 'That password is too long.',
		'password.weak.common': 'That password is too easy to guess. Choose another one.',
		'password.weak.breached': 'That password appeared in a data breach. Choose another one.',
		'password.weak.same': 'Choose a password you have not used here.',
		'password.weak.provider': 'Choose a stronger password.',
		'password.wrong': 'That password is not right.',

		'reset.others': 'Sign out of every other device',
		'acting.end': 'Also remove the access of AI apps and access tokens',
		'acting.note': 'They stop acting for you within a minute. You connect each app again from it, and create new tokens.',
		'acting.review': 'See them first in {apps} and {tokens}.',
		'acting.apps': { one: 'one AI app', other: '{count} AI apps' },
		'acting.tokens': { one: 'one access token', other: '{count} access tokens' },
		'acting.ended': 'Access removed: {what}.',
		'acting.and': ' and ',
		'acting.none': 'No AI app or access token had access.',
		'reset.ended': { one: 'Your password is set. One other session was signed out.', other: 'Your password is set. {count} other sessions were signed out.' },
		'reset.ask': 'Ask for a new link',

		'confirm.resend.lead': 'Enter your address to get a new link.',

		'password.title': 'Password and email',
		'password.change': 'Change password',
		'password.current': 'Current password',
		'password.new': 'New password',
		'password.others': 'Sign out of other devices',
		'password.changed': { one: 'Password changed. One other session was signed out.', other: 'Password changed. {count} other sessions were signed out.' },
		'password.changed.kept': 'Password changed.',
		'password.add': 'Add email sign-in',
		'password.add.lead': 'Sign in with {provider} once more to confirm it is you, then choose a password.',
		'password.add.choose': 'Choose a password for {email}',
		'password.add.plain': 'Choose a password.',
		'password.added': 'You can now sign in with your email and this password too.',
		'email.change': 'Change email',
		'email.new': 'New email address',
		'email.pending': 'Check {email} and open the link to confirm the change.',
		'email.current': 'Signs in as {email}'
	},
	es: {
		'signin.create': 'Crear una cuenta',
		'signin.unconfirmed': 'Primero confirma tu dirección. Abre el enlace que enviamos a {email}.',
		'signin.resend': 'Enviar el enlace otra vez',
		'signin.resend.sent': 'Enviado. Revisa {email}, también la carpeta de spam.',
		'signin.resend.wait': { one: 'Puedes enviarlo otra vez en 1 segundo.', other: 'Puedes enviarlo otra vez en {count} segundos.' },
		'signin.recover.wait': { one: 'Puedes pedirlo otra vez en 1 segundo.', other: 'Puedes pedirlo otra vez en {count} segundos.' },
		'signin.challenge': 'Comprobando este navegador antes del próximo intento…',
		'signin.challenge.short': 'Fallaron varios intentos. Vuelve a intentarlo.',
		'signin.matches': 'Ya existe una cuenta con esta dirección. Inicia sesión con {methods} y luego añade {provider} desde Acceso y seguridad.',
		'signin.matches.short': 'Ya existe una cuenta con esta dirección. Inicia sesión como lo hiciste antes.',
		'signin.erased.at': 'Esta cuenta se eliminó el {date}.',
		'signin.erased.short': 'Esta cuenta se eliminó.',
		'signin.or.email': 'o con tu correo',
		'identities.provider.another': 'el nuevo método de acceso',

		'signup.title': 'Crea tu cuenta de Beyond',
		'signup.lead': 'Una cuenta para todos los productos de Beyond.',
		'signup.name': 'Tu nombre',
		'signup.name.help': 'Lo que ven los demás miembros de tus organizaciones.',
		'signup.submit': 'Crear cuenta',
		'signup.have': '¿Ya tienes una cuenta?',
		'signup.signin': 'Inicia sesión',
		'signup.sent': 'Revisa {email} y abre el enlace para terminar de crear tu cuenta.',

		'password.show': 'Mostrar contraseña',
		'password.hide': 'Ocultar contraseña',
		'password.rule.length': 'Al menos {min} caracteres',
		'password.rule.common': 'Que no sea una contraseña común ni filtrada',
		'password.weak': 'Elige una contraseña más segura.',
		'password.weak.short': 'Usa al menos {min} caracteres.',
		'password.weak.long': 'Esa contraseña es demasiado larga.',
		'password.weak.common': 'Esa contraseña es demasiado fácil de adivinar. Elige otra.',
		'password.weak.breached': 'Esa contraseña apareció en una filtración de datos. Elige otra.',
		'password.weak.same': 'Elige una contraseña que no hayas usado aquí.',
		'password.weak.provider': 'Elige una contraseña más segura.',
		'password.wrong': 'Esa contraseña no es correcta.',

		'reset.others': 'Cerrar sesión en todos los demás dispositivos',
		'acting.end': 'Quitar también el acceso de las apps de IA y los tokens de acceso',
		'acting.note': 'Dejan de actuar por ti en menos de un minuto. Vuelves a conectar cada app desde ella y creas tokens nuevos.',
		'acting.review': 'Míralos antes en {apps} y {tokens}.',
		'acting.apps': { one: 'una app de IA', other: '{count} apps de IA' },
		'acting.tokens': { one: 'un token de acceso', other: '{count} tokens de acceso' },
		'acting.ended': 'Acceso quitado: {what}.',
		'acting.and': ' y ',
		'acting.none': 'Ninguna app de IA ni token de acceso tenía acceso.',
		'reset.ended': { one: 'Tu contraseña está lista. Se cerró otra sesión.', other: 'Tu contraseña está lista. Se cerraron otras {count} sesiones.' },
		'reset.ask': 'Pedir un enlace nuevo',

		'confirm.resend.lead': 'Escribe tu dirección para recibir un enlace nuevo.',

		'password.title': 'Contraseña y correo',
		'password.change': 'Cambiar contraseña',
		'password.current': 'Contraseña actual',
		'password.new': 'Contraseña nueva',
		'password.others': 'Cerrar sesión en los demás dispositivos',
		'password.changed': { one: 'Contraseña cambiada. Se cerró otra sesión.', other: 'Contraseña cambiada. Se cerraron otras {count} sesiones.' },
		'password.changed.kept': 'Contraseña cambiada.',
		'password.add': 'Añadir inicio de sesión con correo',
		'password.add.lead': 'Inicia sesión con {provider} una vez más para confirmar que eres tú y luego elige una contraseña.',
		'password.add.choose': 'Elige una contraseña para {email}',
		'password.add.plain': 'Elige una contraseña.',
		'password.added': 'Ahora también puedes iniciar sesión con tu correo y esta contraseña.',
		'email.change': 'Cambiar correo',
		'email.new': 'Dirección de correo nueva',
		'email.pending': 'Revisa {email} y abre el enlace para confirmar el cambio.',
		'email.current': 'Inicia sesión como {email}'
	}
};
